import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import 'react-flow-renderer/dist/style.css';
import { QueryClient, QueryClientProvider } from "react-query";
import { enUS, esES, frFR, itIT, ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";
import UpdateNotification from "./components/UpdateNotification";
import ImpersonationBanner from "./components/ImpersonationBanner";
import { i18n } from "./translate/i18n";

const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage = localStorage.getItem("primaryColorLight") || localStorage.getItem("primaryColorDark") || "#1e3a5f";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");
  const [primaryColorLight, setPrimaryColorLight] = useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] = useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();
  const resolveAssetUrl = (fileValue, fallback) => {
    if (!fileValue) return fallback;
    if (typeof fileValue === "string" && /^https?:\/\//i.test(fileValue)) {
      return fileValue;
    }
    const backendUrl = getBackendUrl() || (typeof window !== "undefined" ? window.location.origin : "");
    if (!backendUrl) return fallback;
    if (typeof fileValue === "string" && fileValue.startsWith("/")) {
      return `${backendUrl}${fileValue}`;
    }
    return `${backendUrl}/public/${fileValue}`;
  };
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
              backgroundColor: mode === "light" ? primaryColorLight : primaryColorDark,
            },
          },
          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "#F3F3F3" : "#333333",
            },
          },
          palette: {
            type: mode,
            primary: { main: mode === "light" ? primaryColorLight : primaryColorDark },
            textPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
            borderPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
            dark: { main: mode === "light" ? "#333333" : "#F3F3F3" },
            light: { main: mode === "light" ? "#F3F3F3" : "#333333" },
            fontColor: mode === "light" ? primaryColorLight : primaryColorDark,
            tabHeaderBackground: mode === "light" ? "#EEE" : "#666",
            optionsBackground: mode === "light" ? "#fafafa" : "#333",
            fancyBackground: mode === "light" ? "#fafafa" : "#333",
            total: mode === "light" ? "#fff" : "#222",
            messageIcons: mode === "light" ? "grey" : "#F3F3F3",
            inputBackground: mode === "light" ? "#FFFFFF" : "#333",
            barraSuperior: mode === "light" ? primaryColorLight : "#666",
          },
          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (appLogoDark === defaultLogoDark && appLogoLight !== defaultLogoLight) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (appLogoDark !== defaultLogoDark && appLogoLight === defaultLogoLight) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, locale, mode, primaryColorDark, primaryColorLight]
  );

  useEffect(() => {
    const toMuiLocale = (lng) => {
      const l = String(lng || "").toLowerCase();
      if (l.startsWith("pt")) return ptBR;
      if (l.startsWith("es")) return esES;
      if (l.startsWith("fr")) return frFR;
      if (l.startsWith("it")) return itIT;
      return enUS;
    };

    const applyMuiLocale = (lng) => setLocale(toMuiLocale(lng));

    // Apply once on start + keep UI in sync when changing language.
    applyMuiLocale(i18n.language || localStorage.getItem("i18nextLng") || navigator.language);
    i18n.on("languageChanged", applyMuiLocale);

    return () => {
      i18n.off("languageChanged", applyMuiLocale);
    };
  }, []);

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    // Skip settings loading on public pages (login, signup, etc.)
    const publicPaths = ['/', '/login', '/signup', '/forgot-password', '/reset-password'];
    const currentPath = window.location.pathname;
    if (publicPaths.includes(currentPath)) {
      return;
    }


    getPublicSetting("primaryColorLight")
      .then((response) => {
        const color = typeof response === "string" ? response : (response?.value || "#0000FF");
        setPrimaryColorLight(color);
      })
      .catch(() => { });
    getPublicSetting("primaryColorDark")
      .then((response) => {
        const color = typeof response === "string" ? response : (response?.value || "#39ACE7");
        setPrimaryColorDark(color);
      })
      .catch(() => { });
    getPublicSetting("appLogoLight")
      .then((response) => {
        const file = response?.value || response;
        setAppLogoLight(resolveAssetUrl(file, defaultLogoLight));
      })
      .catch(() => { });
    getPublicSetting("appLogoDark")
      .then((response) => {
        const file = response?.value || response;
        setAppLogoDark(resolveAssetUrl(file, defaultLogoDark));
      })
      .catch(() => { });
    getPublicSetting("appLogoFavicon")
      .then((response) => {
        const file = response?.value || response;
        setAppLogoFavicon(resolveAssetUrl(file, defaultLogoFavicon));
      })
      .catch(() => { });
    getPublicSetting("appName")
      .then((response) => {
        const name = response?.value || response;
        setAppName(name || "Multizap Plus");
      })
      .catch(() => {
        setAppName("Multizap Plus");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--primaryColor", mode === "light" ? primaryColorLight : primaryColorDark);
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (_) { }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon url={resolveAssetUrl(appLogoFavicon, defaultLogoFavicon)} />
      {/* UpdateNotification removed - too invasive */}
      <ImpersonationBanner />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
  <div style={{ position: "relative", overflow: "visible", zIndex: 0, minHeight: "100vh" }}>
    <Routes />
  </div>
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
